import React, { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';

import StarBorderRoundedIcon from '@material-ui/icons/StarBorderRounded';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableSortLabel
} from '@material-ui/core';

import {
  CircularLoader,
} from 'components';

import {
  BookedAddOns,
  getRentalItemsBookingTotalInfo
} from 'utils/bookings/prepareBookingsData';

import { EditIcon } from 'components/icons';
import Pagination from 'components/pagination/Pagination';
import BookingsDetailsDialogDialog from 'components/booking-details-dialog/BookingDetailsDialog';
import ListPopover from 'components/list-popover/ListPopover';
import NumberIcon from 'components/number-icon/NumberIcon';

import ConfirmContext from 'contexts/ConfirmContext';

import {

  RENTAL_ITEMS_BOOKINGS
} from 'graphql/queries/bookings';

import {
  CANCEL_RENTAL_ITEMS_BOOKING
} from 'graphql/mutations/booking';

import useStyles from './styles';
import { InfoOutlined } from '@material-ui/icons';

const headingsConfig = classes => {
  return [
    {
      label: 'Reference • Date',
      align: 'left',
      sortBy: 'creation_time',
      cellClassName: classes.referenceCell
    },
    {
      label: 'Booking name',
      sortBy: 'guest_first_name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Rental Items',
      sortBy: 'title',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'NO. of Units',
      sortBy: 'units',
      cellClassName: classes.guestsCell
    },
    {
      label: 'From Date',
      sortBy: 'from_date',
      cellClassName: classes.checkInCell,
      align: 'left'
    },
    {
      label: '',
      cellClassName: classes.arrowCell,
      align: 'left',
      sortBy: ''
    },
    {
      label: 'To Date',
      sortBy: 'to_date',
      cellClassName: classes.checkOutCell,
      align: 'left'
    },
    {
      label: 'Platform',
      sortBy: 'platform',
      cellClassName: classes.platformCell,
      align: 'left'
    },
    {
      label: 'Add-ons',
      cellClassName: classes.addOnsCell,
      sortBy: '',
      align: 'left'
    },
    {
      label: 'Notes',
      cellClassName: classes.notesCell,
      sortBy: '',
      align: 'left'
    },
    { label: 'Actions', sortBy: '', align: 'right' }
  ];
};


function RentalItemsBookingsListTable({
  businessOwner,
  search
}) {
  const classes = useStyles();
  const [t] = useTranslation();

  const confirm = React.useContext(ConfirmContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});

  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const onClose = () => {
    setOpen(false);
  };

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('creation_time');

  const [cancelRentalItemsBooking] = useMutation(CANCEL_RENTAL_ITEMS_BOOKING);



  const [page, setPage] = React.useState(0);

  const variables = {
    page: page,
    orderBy: order === 'asc' ? orderBy : `-${orderBy}`,
    businessOwnerId:
      businessOwner.selectedBusinessOwner.ownerId ||
      businessOwner.selectedBusinessOwner.id,
    search: search || '',
    isCanceled : false
  };

  const { data, loading: isLoading, refetch } = useQuery(RENTAL_ITEMS_BOOKINGS, {
    variables
  });

  
  

 const RENTAL_ITEMS_TYPE = 'Rental Items';

  
  const rentalItemsCancellationHandler = item => {
    confirm({
      title: `Cancel Booking?`,
      description: `${item.fromDate ? item.fromDate : '-'} 
      • ${item.guestFirstName} ${item.guestLastName} • ${item.rentalItems.title}`,
      confirmationText: 'Cancel Booking',
      cancellationText: 'Back',
      translate: false
    }).then(() =>
      cancelRentalItemsBooking({ variables: { ids: [item.id] } }).then(() => {
        enqueueSnackbar(t('Rental Items booking was successfully canceled!'), {
          variant: 'success'
        });

        if (data.length <= 1 && page) {
          refetch({
            page: page - 1
          });
          setPage(page - 1);
        } else {
          refetch({ page });
        }
      })
    );
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  React.useEffect(() => {
    if (search === undefined) return;
    setPage(0);
  }, [search]);

  React.useEffect(() => {
      setPage(0);
      refetch();
    }, [refetch]);

  return (
    <>
    <CircularLoader
            isLoading={isLoading}
            loaderClassName={classes.loaderClassName}
        />
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                style={{ paddingBottom: 15 }}
                className={classNames(classes.tableCell, heading.cellClassName)}
                sortDirection={orderBy === heading.label ? order : false}
              >
                <TableSortLabel
                  active={orderBy === heading.sortBy && orderBy !== ''}
                  direction={orderBy === heading.sortBy ? order : 'asc'}
                  onClick={createSortHandler(heading.sortBy)}
                  className={classes.heading}
                  disabled={heading.sortBy === ''}
                  hideSortIcon={heading.sortBy === ''}
                >
                  {t(heading.label)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.rentalItemsBookings.map(booking => (
              <TableRow
                key={booking.id}
                classes={{ root: classes.tableRow }}
                onClick={e => {
                  if (booking.deactivationBooking) return;

                  e.stopPropagation();
                  openInfoDialog(booking);
                }}
                className={classNames({
                  [classes.disabledArea]: booking.deactivationBooking
                })}
              >
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.referenceCell
                  )}
                  align="left"
                >
                  <Typography>{booking.referenceNumber}</Typography>
                  <Typography>{booking.creationTime}</Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  <Typography className={classes.nameCell}>
                    {booking.deactivationBooking
                      ? 'Deactivation Booking'
                      : `${booking.guestFirstName} ${booking.guestLastName}`}
                  </Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.unitCell)}
                  align="left"
                >
                  <div className={classes.unitContent}>
                    {booking.bookingType === RENTAL_ITEMS_TYPE && (
                      <StarBorderRoundedIcon
                        fontSize="small"
                        className={classes.unitIcon}
                      ></StarBorderRoundedIcon>
                    )}

                    <Typography className={classes.title}>
                      {booking.bookingType === RENTAL_ITEMS_TYPE &&
                        booking.rentalItems.title}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.guestsCell)}
                  align="left"
                >
                  <div className={classes.guestsContent}>
                    {booking.units.length === 0
                      ? '—'
                      : booking.units[0].unitsNumber}
                  </div>
                </TableCell>
                <TableCell
                style={{ paddingBottom: 5, paddingTop: 5 }}
                className={classNames(classes.tableCell, classes.checkInCell)}
                align="left"
              >
                {booking.bookingType === RENTAL_ITEMS_TYPE && booking.toDate && (
                  booking.rentalItems?.pricesType === "pricesHourlyForm"
                ? (booking.startTime
                    ? moment(booking.startTime).format("YYYY-MM-DD HH:mm")
                    : moment(booking.fromDate).format("YYYY-MM-DD"))
                : moment(booking.fromDate).format("YYYY-MM-DD")
            )}
              </TableCell>

              <TableCell
                style={{ paddingBottom: 5, paddingTop: 5 }}
                className={classNames(classes.tableCell)}
                align="left"
              >
                <TrendingFlatIcon
                  style={{ fill: '#9B9B9B', width: '25px', height: '20px' }}
                />
              </TableCell>

              <TableCell
                style={{ paddingBottom: 5, paddingTop: 5 }}
                className={classNames(classes.tableCell, classes.checkInCell)}
                align="left"
              >
                {booking.bookingType === RENTAL_ITEMS_TYPE && booking.toDate && (
                  booking.rentalItems?.pricesType === "pricesHourlyForm"
                ? (booking.endTime
                    ? moment(booking.endTime).format("YYYY-MM-DD HH:mm")
                    : moment(booking.toDate).format("YYYY-MM-DD"))
                : moment(booking.toDate).format("YYYY-MM-DD")
            )}
              </TableCell>

                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.platformCell
                  )}
                  align="left"
                >
                  {t(booking.platform)}
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5 }}
                  className={classNames(classes.tableCell, classes.addOnsCell)}
                  align="left"
                >
                  {booking.bookedAddOns.length <= 2 && (
                    <Typography className={classes.addOnTitle}>
                      {BookedAddOns(booking.bookedAddOns)}
                    </Typography>
                  )}
                  {booking.bookedAddOns.length > 2 && (
                    <Typography className={classes.addOnTitle}>
                      {BookedAddOns(booking.bookedAddOns.slice(0, 2))} ...
                    </Typography>
                  )}

                  {booking.bookedAddOns.length > 2 && (
                    <ListPopover
                      list={
                        <ul className={classes.list}>
                          {booking.bookedAddOns.slice(2).map((o, index) => {
                            return (
                              <li key={index}>
                                {o.addOnCount} x {o.addOn[0].title}
                              </li>
                            );
                          })}
                        </ul>
                      }
                    >
                      <NumberIcon number={booking.bookedAddOns.length - 2} />
                    </ListPopover>
                  )}
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5 }}
                  className={classNames(classes.tableCell, classes.notesCell)}
                  align="left"
                >
                  <ListPopover
                    maxWidth={300}
                    list={
                      <div className={classes.list} style={{ minWidth: 120 }}>
                        {booking.bookingNotes ||
                          booking.activityNotes ||
                          'There are no notes yet.'}
                      </div>
                    }
                  >
                    <InfoOutlined />
                  </ListPopover>
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classes.tableCell}
                  align="right"
                >
                  <div style={{ display: 'inline-flex', paddingRight: '10px' }}>
                    <IconButton
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                      onClick={e => {
                        e.stopPropagation();
                        if (booking.rentalItems) {
                          history.push(
                            `/rental-items-bookings/${booking.id}/edit`
                          );
                        } 
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={
                        booking.bookingType === RENTAL_ITEMS_TYPE
                          ? e => {
                              e.stopPropagation();
                              rentalItemsCancellationHandler(booking);
                            }
                          : e => {
                              null;
                            }
                      }
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      className={classes.iconButton}
                      aria-label="delete"
                    >
                      <HighlightOffIcon className={classes.closeIcon} />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}

          <BookingsDetailsDialogDialog
            onClose={onClose}
            open={open}
            booking={dialogContent}
            bookingTotal={
              dialogContent.bookingType === RENTAL_ITEMS_TYPE
                ? getRentalItemsBookingTotalInfo(dialogContent)
                : null
            }
            refetchParams={{ query: data, variables }}
          />
        </TableBody>
      </Table>
      {data && (
        <Pagination
          page={page + 1}
          count={data.numberOfBookingsRentalItemsPages}
          onChange={onPageChangeHandler}
          amount={`${page * 11 + 1}-${Math.min(
            (page + 1) * 11,
            data.rentalItemsBookingsOverview.currentBookingsNumber
          )}`}
          total={data.rentalItemsBookingsOverview.currentBookingsNumber}
        />
      )}
    </TableContainer>
    </>
  );
}

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(RentalItemsBookingsListTable);
